import Button from "@components/ui/buttons/Button";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { useState, useEffect } from "react";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import {
  MemberAssignmentItem,
  orgMembersToUsers,
} from "@components/ui/blocks/MemberAssignmentBlock";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import HelpTooltip from "@components/ui/HelpTooltip";

export const Automatismes = () => {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const updateOrgData = useDataStore((state) => state.updateOrgData);
  const orgMembers = useDataStore((state) => state.orgMembers);

  const users = orgMembersToUsers(
    orgMembers.filter((m) => m.config?.access_type !== "GUEST"),
  );

  const defaultTeam = orgData?.config?.default_team;

  const [dessinateurId, setDessinateurId] = useState(defaultTeam?.dessinateur_id || "");
  const [savId, setSavId] = useState(defaultTeam?.sav_id || "");
  const [comptabilityId, setComptabilityId] = useState(
    defaultTeam?.comptability_id || "",
  );
  const [communicationId, setCommunicationId] = useState(
    defaultTeam?.communication_id || "",
  );
  const [autoAssignCommercial, setAutoAssignCommercial] = useState(
    defaultTeam?.auto_assign_commercial ?? true,
  );

  useEffect(() => {
    if (orgData?.config?.default_team) {
      const dt = orgData.config.default_team;
      setDessinateurId(dt.dessinateur_id || "");
      setSavId(dt.sav_id || "");
      setComptabilityId(dt.comptability_id || "");
      setCommunicationId(dt.communication_id || "");
      setAutoAssignCommercial(dt.auto_assign_commercial ?? true);
    }
  }, [orgData?.id]);

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!orgData?.id) return;
    setIsSaving(true);
    try {
      await updateOrgData(orgData.id, {
        config: {
          default_team: {
            dessinateur_id: dessinateurId || null,
            sav_id: savId || null,
            comptability_id: comptabilityId || null,
            communication_id: communicationId || null,
            auto_assign_commercial: autoAssignCommercial,
          },
        },
      });
      showNotification({
        type: "success",
        message: "Automatismes enregistrés avec succès",
      });
    } catch {
      showNotification({
        type: "error",
        message: "Échec de l'enregistrement",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getUserInfo = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    return user
      ? { name: user.name, color: user.color }
      : { name: "Ajouter", color: undefined };
  };

  const disabled = !isOwnerOrAdmin(userData);

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Automatismes</Text1>
          <Text2>
            Configurez les valeurs par défaut pour la création de projets.
          </Text2>
          <Text3>
            Ces membres seront automatiquement assignés aux nouveaux projets.
          </Text3>
        </div>

        <div className="flex flex-col gap-4 bg-white p-4 rounded-lg border-1">
          <div className="text-h2 font-semibold text-neutral-700">
            <div className="flex flex-row items-center gap-1.5">
              <span>Équipe par défaut</span>
              <HelpTooltip content="Ces membres sont automatiquement ajoutés à l'équipe de chaque nouveau projet créé dans l'organisation. Vous pouvez toujours les modifier manuellement par projet après création." />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <MemberAssignmentItem
              label="Dessinateur"
              assignMemberToRole={(userId) => !disabled && setDessinateurId(userId)}
              users={users}
              currentUserId={dessinateurId || undefined}
              memberName={getUserInfo(dessinateurId).name}
              memberColor={getUserInfo(dessinateurId).color}
              unassigned={!dessinateurId}
            />
            <MemberAssignmentItem
              label="SAV"
              assignMemberToRole={(userId) => !disabled && setSavId(userId)}
              users={users}
              currentUserId={savId || undefined}
              memberName={getUserInfo(savId).name}
              memberColor={getUserInfo(savId).color}
              unassigned={!savId}
            />
            <MemberAssignmentItem
              label="Comptabilité"
              assignMemberToRole={(userId) =>
                !disabled && setComptabilityId(userId)
              }
              users={users}
              currentUserId={comptabilityId || undefined}
              memberName={getUserInfo(comptabilityId).name}
              memberColor={getUserInfo(comptabilityId).color}
              unassigned={!comptabilityId}
            />
            <MemberAssignmentItem
              label="Communication"
              assignMemberToRole={(userId) =>
                !disabled && setCommunicationId(userId)
              }
              users={users}
              currentUserId={communicationId || undefined}
              memberName={getUserInfo(communicationId).name}
              memberColor={getUserInfo(communicationId).color}
              unassigned={!communicationId}
            />
          </div>

          <div className="border-t border-neutral-200 pt-4 mt-2">
            <SwitchBrick
              title={
                <div className="flex flex-row items-center gap-1.5">
                  <span>Assigner automatiquement le commercial</span>
                  <HelpTooltip content="Lorsque ce paramètre est activé, le créateur d'un nouveau projet est automatiquement défini comme commercial du projet. Utile si la plupart de vos projets sont créés par la personne qui en est responsable." />
                </div>
              }
              description="Le créateur du projet sera automatiquement assigné comme commercial"
              checked={autoAssignCommercial}
              onChange={(checked) => !disabled && setAutoAssignCommercial(checked)}
            />
          </div>

          <Button
            className="mt-4"
            label={isSaving ? "Enregistrement..." : "Enregistrer"}
            onClick={handleSave}
            disabled={isSaving || disabled}
          />
        </div>

      </div>
    </div>
  );
};
